import { useEffect, useRef, useState, useCallback } from "react";
import TextInput from "../TextInput/TextInput";

interface Props {
  item?: any;
  onClose?: any;
  onSave?: any;
  isCategory?: boolean;
  handleHasName?: any;
  isEdit?: boolean;
  updateCategory?: any;
  metaType?: string;
  bunchCategoryList?: any;
  bunchLanguageList?: any;
}

const allowedSubcategoryTypes = ["category", "genre"];

const AddNew = ({
  item,
  onClose,
  onSave,
  handleHasName,
  isEdit,
  updateCategory,
  metaType,
  bunchCategoryList,
  bunchLanguageList,
}: Props) => {
  const [addSubCategory, setAddSubCategory] = useState(isEdit ? true : false);
  const [subcategoryList, setSubcategoryList] = useState<any>([]);
  const [subcategoryInputs, setSubcategoryInputs] = useState<string[]>([""]);
  const [nameError, setNameError] = useState<string>("");
  const [nameTouched, setNameTouched] = useState<boolean>(false);

  // Refs to store latest state values
  const subcategoryListRef = useRef<any>([]);
  const subcategoryInputsRef = useRef<string[]>([""]);
  const catNameRef = useRef<string>("");

  // Track original values for change detection
  const [originalName, setOriginalName] = useState("");
  const [originalSubcategories, setOriginalSubcategories] = useState<string[]>(
    []
  );
  const [_hasChanges, setHasChanges] = useState(false);

  const [name, setName] = useState(isEdit ? item?.name || "" : "");

  // Callback to check for changes and update handleHasName
  const checkForChanges = useCallback(() => {
    const nameChanged = name !== originalName;
    const subcategoriesChanged =
      JSON.stringify(subcategoryInputs) !==
      JSON.stringify(originalSubcategories);
    const hasAnyChanges = nameChanged || subcategoriesChanged;

    setHasChanges(hasAnyChanges);

    if (handleHasName) {
      handleHasName(hasAnyChanges && !nameError);
    }
  }, [
    name,
    originalName,
    subcategoryInputs,
    originalSubcategories,
    handleHasName,
    nameError,
  ]);

  useEffect(() => {
    catNameRef.current = item?.name || "";
  }, []);

  useEffect(() => {
    if (isEdit) {
      const list = item?.sub_meta?.map((item: any) => {
        return item.name;
      });
      if (list?.length > 0) {
        list.push("");
      }

      setSubcategoryList(list);
      setSubcategoryInputs(list);
      subcategoryListRef.current = list;
      subcategoryInputsRef.current = list;

      // Set original values for change tracking
      setOriginalName(item?.name || "");
      setOriginalSubcategories(list);
    } else {
      // For new items, set empty original values
      setOriginalName("");
      setOriginalSubcategories([]);
    }
  }, [isEdit, item]);

  // Update refs whenever state changes
  useEffect(() => {
    subcategoryListRef.current = subcategoryList;
  }, [subcategoryList]);

  useEffect(() => {
    subcategoryInputsRef.current = subcategoryInputs;
  }, [subcategoryInputs]);

  useEffect(() => {
    catNameRef.current = name;
  }, [name]);

  // Check for changes whenever name or subcategoryInputs change
  useEffect(() => {
    checkForChanges();
  }, [name, subcategoryInputs, checkForChanges]);

  // Duplicate validation for language/category title
  useEffect(() => {
    const input = (name || "").trim().toLowerCase();

    if (!input) {
      setNameError("");
      return;
    }

    let exists = false;

    if (metaType === "language" && Array.isArray(bunchLanguageList)) {
      exists = bunchLanguageList.some(
        (it: any) => (it?.name || "").trim().toLowerCase() === input
      );
    }

    if (metaType === "category" && Array.isArray(bunchCategoryList)) {
      exists = bunchCategoryList.some(
        (it: any) => (it?.name || "").trim().toLowerCase() === input
      );
    }

    if (isEdit) {
      const originalNorm = (originalName || "").trim().toLowerCase();
      if (input === originalNorm) {
        exists = false;
      }
    }

    setNameError(exists ? `This ${metaType} already exists.` : "");
  }, [
    name,
    metaType,
    bunchLanguageList,
    bunchCategoryList,
    isEdit,
    originalName,
  ]);

  const handleAddSubCategory = () => {
    setAddSubCategory(true);
    if (
      subcategoryInputs?.length === 0 ||
      subcategoryInputs?.length === undefined
    ) {
      setSubcategoryInputs([""]);
      setSubcategoryList([""]);
    } else {
      const newInputs = [...subcategoryInputs, ""];
      setSubcategoryInputs(newInputs);
      setSubcategoryList(newInputs);
    }
  };

  const handleAddSubcategoryToList = (index: number) => {
    if (subcategoryInputs[index].trim()) {
      setSubcategoryList([...subcategoryList]);
      const newInputs = [...subcategoryInputs];
      newInputs.push("");
      setSubcategoryInputs(newInputs);
    }
  };

  const handleRemoveSubcategoryInput = (index: number) => {
    const newInputs = subcategoryInputs.filter((_, i) => i !== index);
    setSubcategoryInputs(newInputs);
    subcategoryListRef.current = newInputs;
  };

  const handleSubcategoryInputChange = (index: number, value: string) => {
    const newInputs = [...subcategoryInputs];
    newInputs[index] = value;
    setSubcategoryInputs(newInputs);
    setSubcategoryList(newInputs);
  };

  const handleSave = async () => {
    if (nameError) return;
    if (
      subcategoryListRef.current &&
      subcategoryListRef.current[subcategoryListRef.current?.length - 1] === ""
    ) {
      subcategoryListRef.current.pop();
    }

    if (
      subcategoryInputsRef.current &&
      subcategoryInputsRef.current[subcategoryInputsRef.current?.length - 1] ===
        ""
    ) {
      subcategoryInputsRef.current.pop();
    }

    let subCategoryList = subcategoryListRef.current;
    subCategoryList = subCategoryList?.map((item: any) => {
      return {
        name: item,
      };
    });

    if (!isEdit) {
      await onSave({
        name: catNameRef.current,
        sub_meta: subCategoryList,
        status: 1,
        type: metaType,
      });
    } else if (isEdit) {
      // item.sub_meta = subCategoryList;
      await updateCategory({
        ...item,
        name: catNameRef.current,
        sub_meta: subcategoryListRef.current ? [...subCategoryList] : [],
        type: metaType,
      });
    }
    setName("");
    setSubcategoryList([]);
    setSubcategoryInputs([""]);
    onClose();
  };

  useEffect(() => {
    const handlesubmit = (e: any) => {
      e.preventDefault();
      handleSave();
    };
    window.addEventListener("saveCategory", handlesubmit);
    return () => {
      window.removeEventListener("saveCategory", handlesubmit);
    };
  }, []);

  useEffect(() => {
    if (isEdit === false) {
      setSubcategoryList([]);
      setSubcategoryInputs([]);
      setAddSubCategory(false);
      setName("");
      setOriginalName("");
      setOriginalSubcategories([]);
      setHasChanges(false);
    }
  }, [isEdit]);

  return (
    <div>
      <div className="p-2">
        <TextInput
          label="Title"
          value={name}
          onChange={(e: any) => {
            setName(e.target.value);
            if (!nameTouched) setNameTouched(true);
          }}
          onBlur={() => setNameTouched(true)}
          error={nameError}
          touched={nameTouched}
          placeholder="Enter title here..."
        />
      </div>

      {allowedSubcategoryTypes.includes(metaType || "") && (
        <div
          className="d-flex justify-content-end "
          style={{ cursor: "pointer", marginRight: "10px" }}
        >
          <span className="text-primary" onClick={handleAddSubCategory}>
            add sub category <i className="bi bi-plus-circle"></i>
          </span>
        </div>
      )}
      {addSubCategory &&
        subcategoryInputs?.map((input, index) => (
          <div
            key={index}
            className="p-2 d-flex gap-1 align-items-center justify-content-between"
          >
            <div className="flex-grow-1">
              <TextInput
                label="Name"
                value={input}
                onChange={(e: any) =>
                  handleSubcategoryInputChange(index, e.target.value)
                }
                characterLimit={20}
                placeholder="Enter sub category name here..."
                name="subCategory"
                onBlur={() => {}}
                showRequired={false}
                width="100%"
              />
            </div>
            {subcategoryInputs?.length - 1 === index && (
              <span
                className="flex-shrink-0"
                onClick={() => handleAddSubcategoryToList(index)}
                style={{ cursor: "pointer" }}
              >
                <i className="bi bi-plus-circle"></i>
              </span>
            )}
            {subcategoryInputs?.length > 1 &&
              index !== subcategoryInputs?.length - 1 && (
                <span
                  className="flex-shrink-0"
                  onClick={() => handleRemoveSubcategoryInput(index)}
                  style={{ cursor: "pointer", marginLeft: "8px" }}
                >
                  <i className="bi bi-dash-circle text-danger"></i>
                </span>
              )}
          </div>
        ))}
    </div>
  );
};

export default AddNew;
